import React, { useState } from "react";
import { EventFiltersSettings } from "@components/EventFilterSettings";
import { useNode } from "@craftjs/core";
import { FormControlLabel, FormGroup, Switch, TextField } from "@mui/material";
import { useEditorStore } from "@state/editor";
import { useThemeStore } from "@state/theme";
import { useAuthStore } from "@state/auth";

interface CustomHeroProps {
  src: string;
  title?: string;
  subtitle?: string;
  gradient?: boolean;
  showAction?: boolean;
  actionText?: string;
  categoryIDs?: string[];
  eventStatus?: string;
  eventID?: string;
  singleEvent?: boolean;
  persistentStream?: boolean;
  keywords?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  sizing?: {
    height: {
      value: string;
      previewVh: string;
    };
  };
}

const HeroContent = React.forwardRef<HTMLDivElement, CustomHeroProps>((props, ref) => {
  const user = useAuthStore((state) => state.user);
  const { textColor, primaryColor: primaryButtonColor } = useThemeStore();
  const isPreview = useEditorStore(({ isPreview }) => isPreview);

  return (
    <div
      className={`relative min-h-min w-full flex flex-col items-center justify-center overflow-hidden`}
      ref={ref}
      style={{
        height: isPreview ? props.sizing?.height.previewVh : props.sizing?.height.value,
      }}
    >
      <img
        src={props.src}
        alt="Hero"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {props.gradient && <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-50"></div>}

      <div className="relative px-[6%] py-[6%] flex flex-col justify-center items-center text-center">
        {props.title && (
          <h1
            className="text-xl md:text-2xl lg:text-3xl font-bold"
            style={{ color: textColor }}
          >
            {props.title}
          </h1>
        )}

        {props.subtitle && (
          <p
            className="text-sm md:text-lg max-w-lg"
            style={{ color: textColor }}
          >
            {props.subtitle}
          </p>
        )}

        {props.showAction && (
          <div
            className="px-10 mt-2 w-min whitespace-nowrap text-center py-2 block font-bold rounded-md shadow-lg hover:shadow"
            style={{
              color: textColor,
              backgroundColor: primaryButtonColor,
            }}
          >
            {props.actionText}
          </div>
        )}
      </div>
    </div>
  );
});

HeroContent.displayName = "HeroContent";

const CustomHero = (props: CustomHeroProps) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const ref = (node: HTMLDivElement | null) => {
    if (node) {
      connect(drag(node));
    }
  };

  return (
    <HeroContent
      {...props}
      ref={ref}
    />
  );
};

export default CustomHero;

const CustomHeroSettings = () => {
  const {
    actions: { setProp },
    src,
    title,
    subtitle,
    gradient,
    showAction,
    actionText,
    eventID,
    singleEvent,
    persistentStream,
    sizing,
  } = useNode((node) => ({
    src: node.data.props.src,
    title: node.data.props.title,
    subtitle: node.data.props.subtitle,
    gradient: node.data.props.gradient,
    showAction: node.data.props.showAction,
    actionText: node.data.props.actionText,
    eventID: node.data.props.eventID,
    singleEvent: node.data.props.singleEvent,
    persistentStream: node.data.props.persistentStream,
    sizing: node.data.props.sizing,
  }));

  const [showSingleEventFilter, setShowSingleEventFilter] = useState<boolean>(singleEvent);
  const [showPersistentStreamFilter, setShowPersistentStreamFilter] = useState<boolean>(Boolean(persistentStream));

  const toggleSingleEventFilter = () => {
    setShowSingleEventFilter((prev) => {
      setProp((prop: CustomHeroProps) => (prop.singleEvent = !prev));
      return !prev;
    });
  };

  const togglePersistentStreamFilter = () => {
    setShowPersistentStreamFilter((prev) => {
      setProp((prop: CustomHeroProps) => (prop.persistentStream = !prev));
      return !prev;
    });
  };

  return (
    <div className="w-full space-y-4">
      <TextField
        fullWidth
        label="Title"
        value={title}
        onChange={(e) => setProp((prop: CustomHeroProps) => (prop.title = e.target.value))}
      />

      <TextField
        fullWidth
        label="Subtitle"
        value={subtitle}
        onChange={(e) => setProp((prop: CustomHeroProps) => (prop.subtitle = e.target.value))}
      />

      <TextField
        fullWidth
        label="Image URL"
        value={src}
        onChange={(e) => setProp((prop: CustomHeroProps) => (prop.src = e.target.value))}
      />

      {/* <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={gradient}
              onChange={(e) => setProp((prop: CustomHeroProps) => (prop.gradient = e.target.checked))}
            />
          }
          label="Gradient"
        />
      </FormGroup> */}

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={showAction}
              onChange={(e) => setProp((prop: CustomHeroProps) => (prop.showAction = e.target.checked))}
            />
          }
          label="Show Action"
        />

        {showAction && (
          <TextField
            fullWidth
            required
            label="Action Text"
            value={actionText}
            onChange={(e) => setProp((prop: CustomHeroProps) => (prop.actionText = e.target.value))}
          />
        )}
      </FormGroup>

      {showAction && (
        <FormGroup>
          {!showPersistentStreamFilter && (
            <FormControlLabel
              control={
                <Switch
                  checked={showSingleEventFilter}
                  onChange={toggleSingleEventFilter}
                />
              }
              label="Single Event"
            />
          )}

          {!showSingleEventFilter && (
            <FormControlLabel
              control={
                <Switch
                  checked={showPersistentStreamFilter}
                  onChange={togglePersistentStreamFilter}
                />
              }
              label="Persistent Stream"
            />
          )}

          {showAction && (showSingleEventFilter || showPersistentStreamFilter) && (
            <EventFiltersSettings {...(showSingleEventFilter ? { includeSpecificEventID: true } : { includeSpecificPersistentID: true })} />
          )}
        </FormGroup>
      )}

      {showAction && !showSingleEventFilter && !showPersistentStreamFilter && (
        <EventFiltersSettings
          includeCategories
          includeEventStatus
          includeKeywords
          includeDateRange
        />
      )}
    </div>
  );
};

// Default hero image props
CustomHero.craft = {
  props: {
    src: "https://placehold.co/1920",
    title: "Custom Hero",
    subtitle: "Lorem ipsum dolor sit amet consectetur adipisicing elit.",
    gradient: true,
    showAction: false,
    actionText: "Learn More",
    isResizable: true,
    sizing: {
      height: {
        value: "500px",
        previewVh: "50vh",
      },
    },
  },
  related: {
    settings: CustomHeroSettings,
  },
  displayName: "Custom Hero",
};

export const CustomHeroComponent = (props: CustomHeroProps) => {
  return <HeroContent {...props} />;
};
